import { ImageOff, LoaderCircle } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";

import { getToken } from "@/lib/api";
import { cn } from "@/lib/utils";

interface AuthenticatedImageProps {
  src: string;
  alt: string;
  className?: string;
  fallback?: ReactNode;
}

export function AuthenticatedImage({ src, alt, className, fallback }: AuthenticatedImageProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let activeUrl: string | null = null;
    setObjectUrl(null);
    setFailed(false);

    void fetchProtectedImage(src, controller.signal)
      .then((blob) => {
        activeUrl = URL.createObjectURL(blob);
        setObjectUrl(activeUrl);
      })
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) setFailed(true);
      });

    return () => {
      controller.abort();
      if (activeUrl) URL.revokeObjectURL(activeUrl);
    };
  }, [src]);

  if (failed) {
    if (fallback !== undefined) return <>{fallback}</>;
    return (
      <div className={cn("grid min-h-40 place-items-center rounded-2xl bg-muted", className)}>
        <div className="flex flex-col items-center gap-2 text-xs text-muted-foreground">
          <ImageOff className="h-6 w-6" />
          Изображение недоступно
        </div>
      </div>
    );
  }

  if (!objectUrl) {
    if (fallback !== undefined) return <>{fallback}</>;
    return (
      <div className={cn("grid min-h-40 place-items-center rounded-2xl bg-muted", className)}>
        <LoaderCircle className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return <img src={objectUrl} alt={alt} className={className} loading="lazy" />;
}

async function fetchProtectedImage(src: string, signal?: AbortSignal): Promise<Blob> {
  const token = getToken();
  if (!token) throw new Error("not authenticated");
  const response = await fetch(src, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (!response.ok) throw new Error(`image HTTP ${response.status}`);
  return response.blob();
}

export async function authenticatedDownload(src: string, filename: string): Promise<void> {
  const blob = await fetchProtectedImage(src);
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }
}
